// src/services/unitsOfMeasureService.js
//
// Frontend API wrapper for the Units of Measure dictionary in the Catalog domain.
//
// PURPOSE
// -------
// Products now need controlled units for:
// - content/capacity, for example 100 ml
// - stock quantity, for example 10 pcs
//
// The frontend should not hard-code available units in the product page.
// Instead, it reads the backend dictionary:
//   GET /catalog/units-of-measure
//
// This keeps product entry simple for users while preserving clean data for the
// future inventory / warehouse module.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[UnitsOfMeasureService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch units of measure.
 *
 * Backend:
 *   GET /catalog/units-of-measure
 *
 * Optional query params:
 *   - active=true|false
 *   - stock=true|false
 *   - content=true|false
 */
export const fetchUnitsOfMeasure = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/units-of-measure", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchUnitsOfMeasure", err);
  }
};

export default {
  fetchUnitsOfMeasure,
};
